'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import HomepageNav from '@/components/HomepageNav';
import PaymentMarquee from '@/app/components/PaymentMarquee';
import HomepageFooter from '@/app/components/HomepageFooter';
import {
  Clock, Zap, CheckCircle2, AlertCircle, ArrowRight, Shield,
  Smartphone, CreditCard, Globe, Banknote, ChevronDown, ChevronUp,
} from 'lucide-react';

const accent = '#D4AF37';

const cryptoMethods = [
  { name: 'Bitcoin', ticker: 'BTC', img: '/assets/payment/btc.png', color: '#F7931A', network: 'Bitcoin Network', confirmations: '3 confirmations', time: '30 – 60 minutes', fee: 'Network fee only', min: '$50', max: '$200,000', icon: '₿' },
  { name: 'Bitcoin Cash', ticker: 'BCH', img: '/assets/payment/bch.png', color: '#8DC351', network: 'Bitcoin Cash Network', confirmations: '6 confirmations', time: '20 – 40 minutes', fee: 'Network fee only', min: '$50', max: '$200,000', icon: '₿' },
  { name: 'Ethereum', ticker: 'ETH', img: '/assets/payment/eth.png', color: '#627EEA', network: 'Ethereum (ERC-20)', confirmations: '12 confirmations', time: '5 – 15 minutes', fee: 'Gas fee', min: '$50', max: '$200,000', icon: 'Ξ' },
  { name: 'USDT', ticker: 'USDT', img: '/assets/payment/usdt.png', color: '#26A17B', network: 'TRC-20 / ERC-20 / BEP-20', confirmations: '1 – 12 confirmations', time: '1 – 15 minutes', fee: 'Network fee only', min: '$10', max: '$200,000', icon: '₮' },
  { name: 'USDC', ticker: 'USDC', img: '/assets/payment/usdc.png', color: '#2775CA', network: 'ERC-20 / BEP-20', confirmations: '12 confirmations', time: '5 – 15 minutes', fee: 'Gas fee', min: '$10', max: '$200,000', icon: '$' },
  { name: 'TRON', ticker: 'TRX', img: '/assets/payment/trx.png', color: '#EF0027', network: 'TRON Network', confirmations: '19 confirmations', time: '1 – 3 minutes', fee: 'Near zero', min: '$10', max: '$200,000', icon: 'T' },
  { name: 'TRC-20 Tokens', ticker: 'TRC20', img: '/assets/payment/trx.png', color: '#EF0027', network: 'TRON Network', confirmations: '19 confirmations', time: '1 – 3 minutes', fee: 'Near zero', min: '$10', max: '$200,000', icon: 'T' },
  { name: 'ERC-20 Tokens', ticker: 'ERC20', img: '/assets/payment/eth.png', color: '#627EEA', network: 'Ethereum Network', confirmations: '12 confirmations', time: '5 – 15 minutes', fee: 'Gas fee', min: '$10', max: '$200,000', icon: 'Ξ' },
  { name: 'BNB Chain', ticker: 'BNB', img: '/assets/payment/bnb.png', color: '#F3BA2F', network: 'BNB Smart Chain (BEP-20)', confirmations: '15 confirmations', time: '1 – 5 minutes', fee: 'Very low', min: '$10', max: '$200,000', icon: 'B' },
];

const fiatMethods = [
  {
    name: 'M-Pesa',
    color: '#00A859',
    icon: <img src="/assets/payment/mpesa.svg" alt="M-Pesa" className="w-8 h-8 object-contain" />,
    badge: 'INSTANT',
    badgeColor: '#00A859',
    deposit: { time: 'Instant (within seconds)', min: 'KES 500 (~$5)', max: 'KES 300,000 (~$2,300)', fee: '0% we cover fees', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds.' },
    withdrawal: { time: 'Instant (within 1 minute)', min: 'KES 500 (~$5)', max: 'KES 150,000 (~$1,150)/day', fee: '0%', note: 'Withdrawals are processed automatically. You receive an M-Pesa confirmation SMS the moment funds hit your number.' },
    details: [
      'Available for Kenya-registered Safaricom M-Pesa lines.',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same M-Pesa number used to deposit must be used for withdrawal.',
      'Daily limits are set by Safaricom. For higher amounts, contact support to split across multiple transactions.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Cameroon)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'XAF 3,000 (~$5)', max: 'XAF 1,500,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via MTN Mobile Money and Orange Money.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'XAF 3,000 (~$5)', max: 'XAF 750,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet.' },
    details: [
      'Available for Cameroon mobile numbers (MTN, Orange).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Orange Money payments may require OTP verification.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Gabon)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'XAF 3,000 (~$5)', max: 'XAF 1,500,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via MTN Mobile Money and Airtel Money.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'XAF 3,000 (~$5)', max: 'XAF 750,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet.' },
    details: [
      'Available for Gabon mobile numbers (MTN, Airtel).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Daily limits may vary by mobile network provider.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Congo DRC)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'CDF 12,000 (~$5)', max: 'CDF 6,000,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via Airtel Money, Orange Money, and Vodacom M-Pesa.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'CDF 12,000 (~$5)', max: 'CDF 3,000,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet.' },
    details: [
      'Available for DRC mobile numbers (Airtel, Orange, Vodacom).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Daily limits may vary by mobile network provider.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Senegal)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'XOF 3,000 (~$5)', max: 'XOF 1,500,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via Orange Money, Free Money, and Wave.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'XOF 3,000 (~$5)', max: 'XOF 750,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet. Orange Money requires OTP.' },
    details: [
      'Available for Senegal mobile numbers (Orange, Free, Wave).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Orange Money withdrawals require OTP from your Orange Money app.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Côte d\'Ivoire)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'XOF 3,000 (~$5)', max: 'XOF 1,500,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via Orange Money, MTN Mobile Money, and Moov Money.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'XOF 3,000 (~$5)', max: 'XOF 750,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet. Orange Money requires OTP.' },
    details: [
      'Available for Ivory Coast mobile numbers (Orange, MTN, Moov).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Orange Money withdrawals require OTP from your Orange Money app.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Burkina Faso)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'XOF 3,000 (~$5)', max: 'XOF 1,500,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via Orange Money and MTN Mobile Money.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'XOF 3,000 (~$5)', max: 'XOF 750,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet. Orange Money requires OTP.' },
    details: [
      'Available for Burkina Faso mobile numbers (Orange, MTN).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Orange Money withdrawals require OTP from your Orange Money app.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Mali)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'XOF 3,000 (~$5)', max: 'XOF 1,500,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via Orange Money and MTN Mobile Money.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'XOF 3,000 (~$5)', max: 'XOF 750,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet.' },
    details: [
      'Available for Mali mobile numbers (Orange, MTN).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Daily limits may vary by mobile network provider.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Benin)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'XOF 3,000 (~$5)', max: 'XOF 1,500,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via MTN Mobile Money and Moov Money.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'XOF 3,000 (~$5)', max: 'XOF 750,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet.' },
    details: [
      'Available for Benin mobile numbers (MTN, Moov).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Daily limits may vary by mobile network provider.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Togo)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'XOF 3,000 (~$5)', max: 'XOF 1,500,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via MTN Mobile Money and Moov Money.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'XOF 3,000 (~$5)', max: 'XOF 750,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet.' },
    details: [
      'Available for Togo mobile numbers (MTN, Moov).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Daily limits may vary by mobile network provider.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Tanzania)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'TZS 13,000 (~$5)', max: 'TZS 6,500,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via Vodacom M-Pesa, Airtel Money, and Tigo Pesa.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'TZS 13,000 (~$5)', max: 'TZS 3,250,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet.' },
    details: [
      'Available for Tanzania mobile numbers (Vodacom, Airtel, Tigo).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Daily limits may vary by mobile network provider.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Uganda)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'UGX 20,000 (~$5)', max: 'UGX 10,000,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via MTN Mobile Money and Airtel Money.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'UGX 20,000 (~$5)', max: 'UGX 5,000,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet.' },
    details: [
      'Available for Uganda mobile numbers (MTN, Airtel).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Daily limits may vary by mobile network provider.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Nigeria)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'NGN 7,500 (~$5)', max: 'NGN 3,500,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via access Bank PayU, Zenith, and major banks USSD.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'NGN 7,500 (~$5)', max: 'NGN 1,750,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your bank account.' },
    details: [
      'Available for Nigeria bank accounts and mobile wallets.',
      'Same-account in/out policy: the same bank account used to deposit must be used for withdrawal.',
      'Daily limits set by CBN. For higher amounts, contact support.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Niger)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'XOF 3,000 (~$5)', max: 'XOF 1,500,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via Orange Money and Moov Money.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'XOF 3,000 (~$5)', max: 'XOF 750,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet.' },
    details: [
      'Available for Niger mobile numbers (Orange, Moov).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Daily limits may vary by mobile network provider.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Rwanda)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'RWF 7,500 (~$5)', max: 'RWF 3,500,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via MTN Mobile Money and Airtel Money.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'RWF 7,500 (~$5)', max: 'RWF 1,750,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet.' },
    details: [
      'Available for Rwanda mobile numbers (MTN, Airtel).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Daily limits may vary by mobile network provider.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Congo Brazzaville)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'XAF 3,000 (~$5)', max: 'XAF 1,500,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via MTN Mobile Money and Airtel Money.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'XAF 3,000 (~$5)', max: 'XAF 750,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet.' },
    details: [
      'Available for Congo Brazzaville mobile numbers (MTN, Airtel).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Daily limits may vary by mobile network provider.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Guinea Conakry)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'GNF 45,000 (~$5)', max: 'GNF 22,000,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via Orange Money and MTN Mobile Money.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'GNF 45,000 (~$5)', max: 'GNF 11,000,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet.' },
    details: [
      'Available for Guinea mobile numbers (Orange, MTN).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Daily limits may vary by mobile network provider.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Mobile Money (Ghana)',
    color: '#FF6B35',
    icon: <Smartphone size={22} />,
    badge: 'INSTANT',
    badgeColor: '#FF6B35',
    deposit: { time: 'Instant (within seconds)', min: 'GHS 60 (~$5)', max: 'GHS 30,000 (~$2,500)', fee: '2.5% digital charge', note: 'Deposit triggers instantly. Funds reflect in your account within 30 seconds. Available via MTN Mobile Money, Vodafone Cash, and AirtelTigo Money.' },
    withdrawal: { time: 'Instant (within 30 minutes)', min: 'GHS 60 (~$5)', max: 'GHS 15,000 (~$1,250)/day', fee: '2.5%', note: 'Withdrawals are processed automatically. You receive an SMS confirmation when funds hit your mobile wallet.' },
    details: [
      'Available for Ghana mobile numbers (MTN, Vodafone, AirtelTigo).',
      'No bank account required works entirely on mobile.',
      'Same-number in/out policy: the same mobile number used to deposit must be used for withdrawal.',
      'Daily limits may vary by mobile network provider.',
      'Weekend & public holiday withdrawals: still instant we process 24/7.',
    ],
  },
  {
    name: 'Visa & Mastercard',
    color: '#1A1F71',
    icon: <div className="flex gap-1"><img src="/assets/payment/visa.svg" alt="Visa" className="w-6 h-6 object-contain" /><img src="/assets/payment/mastercard.svg" alt="MC" className="w-6 h-6 object-contain" /></div>,
    badge: '3–5 DAYS',
    badgeColor: '#f59e0b',
    deposit: { time: 'Instant to 24 hours', min: '$20', max: '$10,000 per transaction', fee: '2.5% processing fee', note: 'Card deposits are processed by our payment provider. Most cards reflect instantly; some banks may hold for up to 24 hours.' },
    withdrawal: { time: '3 – 5 business days', min: '$20', max: '$5,000 per transaction', fee: '1.5%', note: 'Withdrawals go back to the original card used for deposit. Banks process refunds within 3–5 business days depending on your issuing bank.' },
    details: [
      'Only the card used for deposit is eligible to receive withdrawals (anti-money laundering compliance).',
      'Visa and Mastercard both supported credit and debit.',
      'Card must be in the name of the registered account holder.',
      '3D Secure authentication may be required for large deposits.',
      'International cards accepted from 120+ countries.',
    ],
  },
  {
    name: 'Bank Wire Transfer',
    color: '#3b82f6',
    icon: <Banknote size={22} />,
    badge: '1–3 DAYS',
    badgeColor: '#3b82f6',
    deposit: { time: '1 – 3 business days', min: '$100', max: 'No maximum', fee: "Sender's bank fees apply", note: 'Wire transfers must include your account reference number. Missing reference = delayed processing. Bank details provided on request.' },
    withdrawal: { time: '1 – 3 business days', min: '$100', max: 'No maximum', fee: '$15 flat fee per withdrawal', note: 'SWIFT/IBAN transfers available globally. SEPA transfers within Europe are typically 1 business day. USD wires typically 1–2 days.' },
    details: [
      'Supports SWIFT (international), SEPA (Europe), and local bank transfers.',
      'Bank details shared securely via your dashboard after KYC verification.',
      'Your full legal name on the bank account must match your HiyaPrime account name exactly.',
      'Weekends and public holidays add processing time submit before 2PM UTC on business days for same-day processing.',
      'Large withdrawals (>$50,000) may require additional verification contact support in advance.',
    ],
  },
  {
    name: 'Skrill',
    color: '#862165',
    icon: <img src="/assets/payment/skrill.svg" alt="Skrill" className="w-8 h-8 object-contain" />,
    badge: '1–2 DAYS',
    badgeColor: '#862165',
    deposit: { time: 'Instant to 2 hours', min: '$10', max: '$50,000 per transaction', fee: '1% (capped at $20)', note: 'Skrill deposits are typically instant. Larger amounts may be reviewed by Skrill fraud prevention team, causing minor delays.' },
    withdrawal: { time: '1 – 2 business days', min: '$10', max: '$50,000 per transaction', fee: '1%', note: 'Withdrawals sent to your verified Skrill email address. You must have a verified Skrill account to receive payouts.' },
    details: [
      'Available in 100+ countries.',
      'Skrill email must match your HiyaPrime registered email for security.',
      'Skrill Knect VIP users may receive faster processing.',
      'Currency conversion handled by Skrill at their live rate.',
      'Skrill account must be fully verified (identity documents submitted to Skrill).',
    ],
  },
];

function MethodFAQ({ items }: { items: { q: string; a: string }[] }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="space-y-2 mt-4">
      {items.map((item, i) => (
        <div key={i} className="rounded-lg overflow-hidden" style={{ border: '1px solid #000000' }}>
          <button
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-left"
            style={{ background: '#000000', color: open === i ? '#fff' : '#aaa' }}
            onClick={() => setOpen(open === i ? null : i)}
          >
            {item.q}
            {open === i ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {open === i && (
            <div className="px-4 py-3 text-sm text-muted-foreground leading-relaxed" style={{ background: '#000000' }}>
              {item.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const generalFAQs = [
  { q: 'Are there any withdrawal fees?', a: 'Crypto withdrawals have zero HiyaPrime fees you only pay the blockchain network fee. M-Pesa withdrawals are completely free. Bank wire transfers carry a $15 flat fee. Skrill and card withdrawals carry a 1–1.5% processing fee.' },
  { q: 'How long does KYC verification take?', a: 'KYC (identity verification) typically takes 1–24 hours during business hours. You must complete KYC before your first withdrawal. Document requirements: government-issued ID + selfie. Verification is a one-time process.' },
  { q: 'Can I use multiple payment methods?', a: 'Yes. You can deposit via one method and withdraw via another, with the exception of credit/debit cards withdrawals must return to the original card used for deposit.' },
  { q: 'What currencies are supported?', a: 'We accept USD as the base currency for all accounts. Crypto is converted at live market rates. M-Pesa transactions are in KES. Card transactions support 50+ currencies with automatic conversion.' },
  { q: 'Is there a minimum withdrawal amount?', a: 'Minimums vary by method: Crypto $10–$50 · M-Pesa KES 500 (~$5) · Bank wire $100 · Card $20 · Skrill $10. Profit split payouts must meet a $100 minimum before requesting withdrawal.' },
  { q: 'What happens if my crypto deposit doesn\'t arrive?', a: 'First, verify you sent to the correct wallet address and network. Blockchain transactions are irreversible. If you sent to the correct address and it doesn\'t appear after the expected confirmation time, contact support with your transaction hash (TxID).' },
];

export default function PaymentsPage() {
  return (
    <div className="min-h-screen bg-background">
      <HomepageNav />

      {/* Hero */}
      <section className="py-20 px-6 lg:px-10" style={{ background: 'linear-gradient(180deg, #000000 0%, #000000 100%)', borderBottom: '1px solid #000000' }}>
        <div className="max-w-screen-xl mx-auto text-center space-y-5">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold tracking-wider uppercase" style={{ borderColor: `${accent}40`, background: `${accent}10`, color: accent }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accent }} />
            Secure & Transparent Payments
          </div>
          <h1 className="text-4xl lg:text-6xl font-extrabold tracking-tight text-white">
            Payments &{' '}
            <span style={{ color: accent }}>Withdrawals</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            We support 22 payment methods across crypto, mobile money, cards, and bank transfers.
            Your profits are yours get them fast, your way.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            {[
              { icon: <Zap size={14} />, label: 'Mobile Money Instant', color: '#FF6B35' },
              { icon: <Clock size={14} />, label: 'Crypto 1–60 min', color: '#F7931A' },
              { icon: <Shield size={14} />, label: 'Bank 1–3 days', color: '#3b82f6' },
              { icon: <CheckCircle2 size={14} />, label: 'Cards 3–5 days', color: '#f59e0b' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border" style={{ border: `1px solid ${item.color}40`, background: `${item.color}10`, color: item.color }}>
                {item.icon}
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      <PaymentMarquee />

      {/* Quick Overview */}
      <section className="py-14 px-6 lg:px-10">
        <div className="max-w-screen-xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Processing Times at a Glance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { label: 'Mobile Money', time: 'Instant', sub: 'Under 30 minutes', color: '#FF6B35', icon: <Smartphone size={20} /> },
              { label: 'Crypto', time: '1–60 min', sub: 'Depends on network', color: '#F7931A', icon: <Globe size={20} /> },
              { label: 'Bank Wire', time: '1–3 days', sub: 'Business days only', color: '#3b82f6', icon: <Banknote size={20} /> },
              { label: 'Cards / Skrill', time: '1–5 days', sub: 'Issuer dependent', color: '#f59e0b', icon: <CreditCard size={20} /> },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl p-6 text-center space-y-3" style={{ background: '#000000', border: `1px solid ${item.color}30` }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto" style={{ background: `${item.color}15`, color: item.color }}>
                  {item.icon}
                </div>
                <div className="text-sm text-muted-foreground font-medium">{item.label}</div>
                <div className="text-2xl font-extrabold" style={{ color: item.color }}>{item.time}</div>
                <div className="text-xs text-muted-foreground">{item.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Crypto Section */}
      <section className="py-14 px-6 lg:px-10" style={{ background: '#000000', borderTop: '1px solid #000000', borderBottom: '1px solid #000000' }}>
        <div className="max-w-screen-xl mx-auto space-y-8">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Globe size={22} style={{ color: '#F7931A' }} />
              <h2 className="text-2xl font-bold text-white">Cryptocurrency Payments</h2>
              <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: '#F7931A20', color: '#F7931A' }}>9 NETWORKS</span>
            </div>
            <p className="text-muted-foreground text-sm max-w-2xl">
              Crypto is the fastest way to fund your account and receive payouts. Zero intermediaries, no bank approval required.
              Always send on the correct network cross-network sends are unrecoverable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cryptoMethods.map((c) => (
              <div key={c.ticker} className="rounded-2xl p-5 space-y-4" style={{ background: '#000000', border: `1px solid ${c.color}25` }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden" style={{ background: '#000000', border: `1px solid ${c.color}40` }}>
                    <img src={c.img} alt={c.name} className="w-8 h-8 object-contain" />
                  </div>
                  <div>
                    <div className="font-bold text-white text-sm">{c.name}</div>
                    <div className="text-xs font-mono" style={{ color: c.color }}>{c.ticker}</div>
                  </div>
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Network</span>
                    <span className="text-white font-medium text-right max-w-[180px]">{c.network}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Confirmations</span>
                    <span className="text-white font-medium">{c.confirmations}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Processing time</span>
                    <span className="font-semibold" style={{ color: c.color }}>{c.time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Network fee</span>
                    <span className="text-white font-medium">{c.fee}</span>
                  </div>
                  <div className="pt-2 flex justify-between border-t" style={{ borderColor: '#222' }}>
                    <span className="text-muted-foreground">Min / Max</span>
                    <span className="text-white font-medium">{c.min} – {c.max}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl p-5 flex gap-4" style={{ background: '#F7931A10', border: '1px solid #F7931A30' }}>
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#F7931A' }} />
            <div className="text-sm text-muted-foreground leading-relaxed space-y-1">
              <p className="font-semibold text-white">Important: Always verify the network before sending</p>
              <p>Sending USDT on ERC-20 to a TRC-20 address (or vice versa) will result in permanent loss of funds. HiyaPrime cannot recover cross-network transactions. Always double-check the deposit address and network in your dashboard before sending.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Fiat & Mobile Methods */}
      <section className="py-14 px-6 lg:px-10">
        <div className="max-w-screen-xl mx-auto space-y-12">
          <h2 className="text-2xl font-bold text-white">Mobile Money, Cards & Bank Transfers</h2>

          {fiatMethods.map((method) => (
            <div key={method.name} className="rounded-2xl overflow-hidden" style={{ border: '1px solid #000000' }}>
              {/* Header */}
              <div className="px-6 py-5 flex items-center justify-between" style={{ background: '#000000' }}>
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${method.color}20`, color: method.color }}>
                    {method.icon}
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-lg">{method.name}</h3>
                  </div>
                </div>
                <span className="px-3 py-1.5 rounded-full text-xs font-black tracking-wider" style={{ background: `${method.badgeColor}20`, color: method.badgeColor, border: `1px solid ${method.badgeColor}40` }}>
                  {method.badge}
                </span>
              </div>

              <div className="p-6 space-y-6">
                {/* Deposit / Withdrawal grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="rounded-xl p-5 space-y-4" style={{ background: '#000000', border: '1px solid #000000' }}>
                    <div className="flex items-center gap-2 text-sm font-semibold text-green-400">
                      <ArrowRight size={14} className="rotate-180" />
                      Deposits
                    </div>
                    <div className="space-y-2.5 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Processing time</span>
                        <span className="font-semibold" style={{ color: method.badgeColor }}>{method.deposit.time}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Minimum</span>
                        <span className="text-white">{method.deposit.min}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Maximum</span>
                        <span className="text-white">{method.deposit.max}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Fee</span>
                        <span className="text-white">{method.deposit.fee}</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed pt-2 border-t" style={{ borderColor: '#222' }}>
                      {method.deposit.note}
                    </p>
                  </div>

                  <div className="rounded-xl p-5 space-y-4" style={{ background: '#000000', border: '1px solid #000000' }}>
                    <div className="flex items-center gap-2 text-sm font-semibold text-blue-400">
                      <ArrowRight size={14} />
                      Withdrawals
                    </div>
                    <div className="space-y-2.5 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Processing time</span>
                        <span className="font-semibold" style={{ color: method.badgeColor }}>{method.withdrawal.time}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Minimum</span>
                        <span className="text-white">{method.withdrawal.min}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Maximum</span>
                        <span className="text-white">{method.withdrawal.max}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Fee</span>
                        <span className="text-white">{method.withdrawal.fee}</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed pt-2 border-t" style={{ borderColor: '#222' }}>
                      {method.withdrawal.note}
                    </p>
                  </div>
                </div>

                {/* Details */}
                <div className="rounded-xl p-5 space-y-3" style={{ background: '#000000', border: '1px solid #000000' }}>
                  <div className="text-xs font-semibold text-white uppercase tracking-wider">Key Details</div>
                  <ul className="space-y-2">
                    {method.details.map((d, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                        <CheckCircle2 size={13} className="flex-shrink-0 mt-0.5" style={{ color: method.color }} />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* General FAQ */}
      <section className="py-14 px-6 lg:px-10" style={{ background: '#000000', borderTop: '1px solid #000000' }}>
        <div className="max-w-screen-xl mx-auto space-y-8">
          <h2 className="text-2xl font-bold text-white">Frequently Asked Questions</h2>
          <MethodFAQ items={generalFAQs} />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 lg:px-10 text-center" style={{ borderTop: '1px solid #000000' }}>
        <div className="max-w-xl mx-auto space-y-5">
          <h2 className="text-3xl font-extrabold text-white">Ready to Get Funded?</h2>
          <p className="text-muted-foreground">Start your evaluation challenge today. Pass, get funded, and withdraw your profits the way you prefer.</p>
          <Link href="/register" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white transition-all hover:opacity-90" style={{ background: accent }}>
            Start Your Challenge
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <HomepageFooter />
    </div>
  );
}
